/**
 * Pre-creation sample-size estimate for the experiment creation form.
 *
 * This is the one place in this module where the browser does statistics, and it exists only
 * because there is nothing to ask the backend yet: before an experiment exists, there is no
 * `GetExperimentResults.required_visitors` to read. Once the experiment is running, that
 * backend-computed figure is authoritative and this estimate is never shown again.
 */

/**
 * Baseline conversion rate used by the estimate, as a fraction.
 *
 * 3.6 % is the conversion between interacted sessions measured across the whole prod tracker on
 * 2026-09-14 (1 % sample, last 30 days — see `wit-ops/docs/test-ab-datos-plan-implementacion.md`
 * §1). It is a constant on purpose: the per-hotel figure lives in `mart_engine__conversion`, which
 * the backoffice has no route to, so a single documented average is honest where a per-hotel number
 * would merely look precise. A hotel whose real conversion is far from 3.6 % will see this estimate
 * disagree with the backend's own `required_visitors` — which is why the copy presents it as an
 * approximation.
 */
export const DEFAULT_BASELINE_CONVERSION_RATE = 0.036;

/** z for a two-sided α = 0.05. */
const Z_ALPHA = 1.96;
/** z for 80 % power. */
const Z_BETA = 0.84;

/**
 * Valid visitors needed IN TOTAL (both arms) to detect a relative improvement of `mdePercent` on a
 * baseline conversion of `baseline`, at 95 % confidence and 80 % power:
 *
 *     n = 2 · (z_α + z_β)² · [p(1−p) + p′(1−p′)] / (p′ − p)²      with p′ = p · (1 + mde/100)
 *
 * This is the formula `backoffice-manager` and the `wit-data` marts use (plan §4.2), so the form's
 * estimate and the backend's later figure differ only in their baseline, never in their method.
 * Returns 0 for a non-positive or absurd MDE rather than Infinity/NaN, so the caller can simply
 * decide not to show a hint.
 */
export function requiredValidVisitors(
  mdePercent: number,
  baseline: number = DEFAULT_BASELINE_CONVERSION_RATE
): number {
  if (!Number.isFinite(mdePercent) || mdePercent <= 0) return 0;
  if (!Number.isFinite(baseline) || baseline <= 0 || baseline >= 1) return 0;

  const p = baseline;
  const pPrime = p * (1 + mdePercent / 100);
  if (pPrime >= 1) return 0;

  const delta = pPrime - p;
  const variance = p * (1 - p) + pPrime * (1 - pPrime);
  const total = (2 * Math.pow(Z_ALPHA + Z_BETA, 2) * variance) / Math.pow(delta, 2);

  return Math.ceil(total);
}

/** The same figure, rounded to a readable order of magnitude for display ("~23.000", not 22.998). */
export function roundedRequiredValidVisitors(
  mdePercent: number,
  baseline: number = DEFAULT_BASELINE_CONVERSION_RATE
): number {
  const exact = requiredValidVisitors(mdePercent, baseline);
  if (exact === 0) return 0;
  const magnitude = Math.pow(10, Math.max(Math.floor(Math.log10(exact)) - 2, 0));
  return Math.round(exact / magnitude) * magnitude;
}
